import { useMemo } from "react";

import { useGetAnswersQuery } from "@/redux/service";
import { AnswerResponse } from "@/redux/survey/types";
import { isPaginatedResponse } from "@/redux/typeGuards";
import { SurveyViewDataFormat } from "@/redux/survey-views/types";
import { useOrganizationId } from "@/shared/hooks/useOrganizationId";

export interface UseSurveyViewSelectableAnswersProps {
  surveyId?: number;
  dataFormat: keyof typeof SurveyViewDataFormat;
}

export interface SurveyViewSelectableAnswers {
  isLoading: boolean;
  isError: boolean;
  selectableAnswers: AnswerResponse[];
  answersWithoutBlocks: AnswerResponse[];
  answersWithBlocks: AnswerResponse[];
  answeredQuestionIds: Set<number>;
  totalBlocksPerSharingRight: Record<string, number>;
  totalBlocks: number;
}

/**
 * Single source of truth for the counters and the selection of the survey view
 * form. Both the "selected" and the "answered" side of every counter are derived
 * from this one list, so they can never be built on different rules.
 *
 * The API filters the answers by data format, so every answer returned here
 * belongs to a question that the chosen format includes.
 */
export const useSurveyViewSelectableAnswers = ({
  surveyId,
  dataFormat,
}: UseSurveyViewSelectableAnswersProps): SurveyViewSelectableAnswers => {
  const organizationId = useOrganizationId();

  const answersResponse = useGetAnswersQuery(
    {
      survey: surveyId,
      organization: organizationId,
      dataFormat: SurveyViewDataFormat[dataFormat],
      pagination: "off",
      mode: "base",
    },
    { skip: !surveyId || !organizationId },
  );

  const selectableAnswers = useMemo(() => {
    const data = answersResponse.data;
    if (!data) return [];
    return isPaginatedResponse(data) ? data.results : data;
  }, [answersResponse.data]);

  return useMemo(() => {
    const answersWithBlocks: AnswerResponse[] = [];
    const answersWithoutBlocks: AnswerResponse[] = [];
    const answeredQuestionIds = new Set<number>();
    const totalBlocksPerSharingRight: Record<string, number> = {};
    let totalBlocks = 0;

    selectableAnswers.forEach((answer) => {
      answeredQuestionIds.add(answer.question);

      if (answer.attachedBlocks.length === 0) {
        answersWithoutBlocks.push(answer);
        return;
      }

      answersWithBlocks.push(answer);
      answer.attachedBlocks.forEach((block) => {
        totalBlocksPerSharingRight[block.sharingRights] =
          (totalBlocksPerSharingRight[block.sharingRights] ?? 0) + 1;
        totalBlocks += 1;
      });
    });

    return {
      isLoading: answersResponse.isLoading,
      isError: answersResponse.isError,
      selectableAnswers,
      answersWithoutBlocks,
      answersWithBlocks,
      answeredQuestionIds,
      totalBlocksPerSharingRight,
      totalBlocks,
    };
  }, [selectableAnswers, answersResponse.isLoading, answersResponse.isError]);
};
